import { Link } from 'react-router';
import { cn } from '@/lib/cn';
import { RaceShowcase } from './RaceShowcase';
import { formatWager } from '../format';
import { useLeaderboard, useRaces, type Standing } from '../api';

/** Ranks below the podium that the home page shows before sending you to the full board. */
const RunnersUp = 4;

/**
 * The home page's race panel.
 *
 * Left: what is at stake and how long is left. Right: who is winning, and the
 * names immediately behind them. That split is the owner's design, and it is the
 * right one — the prize answers "why should I care", the podium answers "is it
 * still worth entering", and the runners-up answer "how close is close".
 *
 * The API already orders races by what a visitor cares about — running, then
 * awaiting results, then upcoming — so taking the first is that decision made
 * once, on the server, rather than twice in two places that can drift.
 */
export function HomeRaceSection() {
  const { data, isLoading } = useRaces(1);
  const race = data?.[0];

  // Ranks 4–7 come from the leaderboard's first page rather than a second
  // endpoint. It is one indexed range read, and the query is already defined.
  const leaderboard = useLeaderboard(race?.slug, 1, 3 + RunnersUp);

  if (isLoading) {
    return <div className="h-96 animate-pulse rounded-[var(--radius-card)] bg-surface-900/60" />;
  }

  // No race is a normal state between campaigns, not an error — and the home
  // page reads as broken with a section missing from the middle of it. The card
  // says what is true (there isn't one yet) rather than inventing a date, which
  // would be a promise nobody made.
  if (!race) {
    return <NoRaceCard />;
  }

  const runnersUp = (leaderboard.data?.rows ?? [])
    .filter((row) => row.rank > 3)
    .slice(0, RunnersUp);

  return (
    <section aria-labelledby="wager-race">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h2 id="wager-race" className="section-title flex items-center gap-2">
          <TrophyGlyph />
          Çevrim Yarışması
        </h2>
      </div>

      <RaceShowcase
        race={race}
        action={
          <Link to={`/wager-races/${race.slug}`} className="gf-button gf-button-primary mt-5 w-fit">
            Tüm sıralamayı gör
            <ArrowGlyph />
          </Link>
        }
      >
        {runnersUp.length > 0 ? (
          <ol className="race-runners">
            {runnersUp.map((row) => (
              <RunnerUp key={`${row.rank}-${row.displayName}`} row={row} currency={race.currency} />
            ))}
          </ol>
        ) : null}
      </RaceShowcase>
    </section>
  );
}

/**
 * The placeholder between campaigns.
 *
 * Deliberately carries no countdown and no date. The next race's start is not
 * stored anywhere until an editor creates it, so any figure here would be
 * invented — and a countdown that expires with nothing behind it is worse than
 * the honest sentence.
 */
function NoRaceCard() {
  return (
    <section aria-labelledby="wager-race">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h2 id="wager-race" className="section-title flex items-center gap-2">
          <TrophyGlyph />
          Çevrim Yarışması
        </h2>
      </div>

      <div className="empty-state">
        <span aria-hidden className="empty-state-icon">
          <TrophyGlyph />
        </span>

        <p className="mt-4 text-lg font-extrabold tracking-tight text-surface-100 sm:text-xl">
          Yeni wager yarışması yakında
        </p>

        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-surface-400">
          Bir sonraki yarışma hazırlanıyor. Duyurusunu ilk duymak için Telegram grubuna katıl — ödül
          havuzu ve tarih orada paylaşılıyor.
        </p>

        <Link
          to="/wager-races"
          className={cn(
            'mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg',
            'border border-brand-500/40 px-5 text-sm font-semibold text-brand-400',
            'transition hover:border-brand-500 hover:bg-brand-500/10',
          )}
        >
          Geçmiş yarışmalara bak
          <ArrowGlyph />
        </Link>
      </div>
    </section>
  );
}

function RunnerUp({ row, currency }: { row: Standing; currency: string }) {
  return (
    <li
      className={cn(
        'race-runner flex items-center gap-3 px-4 py-3 text-sm',
        row.isYou ? 'bg-brand-500/10' : 'bg-surface-900',
      )}
    >
      <span className="w-5 shrink-0 font-mono text-xs text-surface-500 tabular-nums">
        {row.rank}
      </span>

      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          row.isYou ? 'font-medium text-brand-400' : 'text-surface-200',
        )}
      >
        {row.displayName}
      </span>

      <span className="shrink-0 font-mono text-xs text-surface-400 tabular-nums">
        {formatWager(row.wagered, currency)}
      </span>
    </li>
  );
}

function TrophyGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
      <path d="M7 4h10v1h3v3a4 4 0 0 1-3.4 4 5 5 0 0 1-2.6 2.7V17h3v3H7v-3h3v-2.3A5 5 0 0 1 7.4 12 4 4 0 0 1 4 8V5h3V4zm10 3v3.9A2 2 0 0 0 18 9V7h-1zM6 7v2a2 2 0 0 0 1 1.9V7H6z" />
    </svg>
  );
}

function ArrowGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
      <path d="M13 5l7 7-7 7v-4H4v-6h9V5z" />
    </svg>
  );
}
